import {
  OPENER_COLOR_LABELS,
  OPENER_COLOR_ROLES,
  type OpenerColorRole,
  type ShopifyOpenerThemeDraft,
  STOREFRONT_RARITY_TIERS,
} from "@loot-os/api-contract";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";

import { ColorField, RadiusField } from "./StorefrontFields";

/**
 * The opener theme's fields: seven color roles, corner radius, font and the rarity colors. A blank
 * field keeps the host's value, which `fallbackFor` names so the swatch shows what will be used.
 * The Theme card of `OpenerThemeEditor`.
 */
export function OpenerThemeFields({
  idPrefix,
  draft,
  onChange,
  fallbackFor,
  fontPlaceholder,
  disabled,
}: {
  idPrefix: string;
  draft: ShopifyOpenerThemeDraft;
  onChange: (next: ShopifyOpenerThemeDraft) => void;
  fallbackFor: (role: OpenerColorRole) => string;
  fontPlaceholder: string;
  disabled: boolean;
}) {
  const edit = (next: Partial<ShopifyOpenerThemeDraft>) => onChange({ ...draft, ...next });
  return (
    <>
      <fieldset className="grid gap-4 sm:grid-cols-2" disabled={disabled}>
        {OPENER_COLOR_ROLES.map((role) => (
          <ColorField
            key={role}
            id={`${idPrefix}-color-${role}`}
            label={OPENER_COLOR_LABELS[role]}
            hint={role}
            fallback={fallbackFor(role)}
            value={draft.colors[role] ?? ""}
            onChange={(value) => edit({ colors: { ...draft.colors, [role]: value } })}
          />
        ))}
      </fieldset>

      {/* The radius control is a slider, a field and a sample, so it takes the full width. */}
      <fieldset className="grid gap-4" disabled={disabled}>
        <RadiusField value={draft.radius} onChange={(radius) => edit({ radius })} />
        <div className="grid content-start gap-2">
          <Label htmlFor={`${idPrefix}-font-family`}>Font family</Label>
          <Input
            id={`${idPrefix}-font-family`}
            value={draft.fontFamily}
            onChange={(e) => edit({ fontFamily: e.target.value })}
            placeholder={fontPlaceholder}
          />
        </div>
      </fieldset>

      <fieldset className="grid gap-4 sm:grid-cols-2" disabled={disabled}>
        <legend className="mb-2 text-sm font-medium">Rarity colors</legend>
        {STOREFRONT_RARITY_TIERS.map((tier) => (
          <ColorField
            key={tier.key}
            id={`${idPrefix}-rarity-${tier.key}`}
            label={tier.label}
            hint={`rarity ${tier.key}`}
            fallback={tier.default}
            value={draft.rarity[tier.key] ?? ""}
            onChange={(value) => edit({ rarity: { ...draft.rarity, [tier.key]: value } })}
          />
        ))}
      </fieldset>
    </>
  );
}
